// src/pages/ColorRestoration.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useProductStore } from "../store/useProductStore";
import GenerateColorRestorationModal from "../components/GenerateColorRestorationModal";
import placeholderImage from "../assets/placeholder.jpg";
import {
  Plus,
  Download,
  Trash2,
  X,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";
import Loader from "../components/Loader";

const FEATURE_KEY = "color-restoration";
const PAGE_SIZE = 12;

export default function ColorRestoration() {
  const { getToken } = useAuth();
  const { selectedProduct } = useProductStore();
  const { projectId: paramProjectId } = useParams();
  const navigate = useNavigate();

  // ✅ Prefer URL param as source of truth (prevents ping-pong when navigating via notifications)
  const projectId = paramProjectId || selectedProduct?.id;

  // items: [{ id, url, status, created_at }]
  const [items, setItems] = useState([]);
  const [productImages, setProductImages] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { id, url }
  const [error, setError] = useState(null);

  const [quota, setQuota] = useState({
    loading: true,
    unlimited: false,
    remaining: 0,
    used: 0,
    baseCap: 0,
    imagesExtra: 0,
    period_start: null,
    period_end: null,
    blocked: false,
    payment_blocked: false,
    is_blocked: false,
    sub_status: null,
  });

  const [projectLoading, setProjectLoading] = useState(true);

  // ✅ infinite scroll state (same pattern as ObjectRemover)
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);

  const previewBackdrop = useRef(null);
  const pollTimerRef = useRef(null);

  /* ----------------------------- Helpers ----------------------------- */
  const toArray = useCallback((val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;

    if (typeof val === "string") {
      const t = val.trim();
      if (!t || t === "{}") return [];

      // postgres array string: "{a,b}"
      if (t.startsWith("{") && t.endsWith("}")) {
        return t
          .slice(1, -1)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }

      try {
        const parsed = JSON.parse(t);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        // if it's a single URL string, return as single-item array
        return [t];
      }
    }

    return [];
  }, []);

  const getMeta = useCallback((m) => {
    if (!m) return {};
    if (typeof m === "string") {
      try {
        return JSON.parse(m);
      } catch {
        return {};
      }
    }
    return m;
  }, []);

  const pickFeatureAssets = useCallback(
    (data) => data?.assetsByFeature?.[FEATURE_KEY] || data?.assets || [],
    []
  );

  const mapAssets = useCallback(
    (rows) => {
      const PENDING = new Set(["pending", "queued", "processing"]);

      return (rows || [])
        .map((a) => {
          const meta = getMeta(a?.metadata);
          const feature = String(meta?.feature || "").toLowerCase();
          const statusRaw = String(meta?.status || "finished").toLowerCase();

          // ✅ Hide soft-deleted + failed rows (they stay in DB for quota, but disappear from UI)
          const isDeleted = meta?.deleted === true || statusRaw === "deleted";
          const isFailed = statusRaw === "failed";
          if (isDeleted || isFailed) return null;

          const rawUrl = a?.url || "";
          const looksPending =
            PENDING.has(statusRaw) ||
            !rawUrl ||
            rawUrl === "/spinner.gif" ||
            String(rawUrl).includes("#");

          return {
            id: a?.id,
            url: looksPending ? "/spinner.gif" : rawUrl,
            status: looksPending ? statusRaw || "pending" : "finished",
            feature,
            created_at: a?.created_at || a?.createdAt || 0,
          };
        })
        .filter(Boolean)
        .filter((x) => x.feature === FEATURE_KEY)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    },
    [getMeta]
  );

  const anyPendingLocal = useMemo(() => {
    const PENDING = new Set(["pending", "queued", "processing"]);
    return items.some(
      (i) => PENDING.has(i.status) || !i.url || i.url === "/spinner.gif"
    );
  }, [items]);

  // ✅ infinite scroll derived list
  const visibleItems = useMemo(
    () => items.slice(0, page * PAGE_SIZE),
    [items, page]
  );

  // ✅ make this stable (DO NOT depend on `page`)
  const recalcHasMore = useCallback((arr, pageNum) => {
    const p = Number(pageNum) || 1;
    setHasMore(p * PAGE_SIZE < (arr?.length || 0));
  }, []);

  // Keep pageRef in sync (so merges can compute hasMore without causing refetch loops)
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // Also keep hasMore in sync whenever page/items change
  useEffect(() => {
    setHasMore(page * PAGE_SIZE < items.length);
  }, [page, items.length]);

  /* ----------------------------- Quota ----------------------------- */
  const fetchQuota = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/color-restoration/quota`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuota({ loading: false, ...res.data });
    } catch {
      setQuota((q) => ({ ...q, loading: false }));
    }
  }, [getToken]);

  /* --------------------------- Fetch project --------------------------- */
  const fetchProject = useCallback(async () => {
    // ✅ If no analyzed project yet, don’t error — just show CTA and empty grid.
    if (!projectId) {
      setProductImages([]);
      setItems((prev) => prev.filter((p) => String(p.id).startsWith("temp-"))); // keep optimistic only
      setError(null);
      setProjectLoading(false);
      setPage(1);
      pageRef.current = 1;
      setHasMore(false);
      return;
    }

    setProjectLoading(true);
    setPage(1);
    pageRef.current = 1;

    try {
      const token = await getToken();
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setProductImages(toArray(data.images));

      const serverList = mapAssets(pickFeatureAssets(data));
      setItems((prev) => {
        const temps = prev.filter((p) => String(p.id).startsWith("temp-"));
        const serverById = new Map(serverList.map((a) => [String(a.id), a]));
        const keptTemps = temps.filter((t) => !serverById.has(String(t.id)));
        const merged = [...keptTemps, ...serverList];
        recalcHasMore(merged, 1);
        return merged;
      });

      setError(null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Fetch project failed:", e?.response?.data || e.message);
      setError(
        e?.response?.status === 401
          ? "Session expired. Please sign in again."
          : "Failed to fetch project data"
      );
    } finally {
      setProjectLoading(false);
    }
  }, [getToken, projectId, toArray, mapAssets, pickFeatureAssets, recalcHasMore]);

  /* ---------------------------- Polling ---------------------------- */
  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const beginPolling = useCallback(() => {
    if (!projectId) return;

    stopPolling();
    let attempts = 0;

    const tick = async () => {
      attempts += 1;
      try {
        const token = await getToken();
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const serverList = mapAssets(pickFeatureAssets(res.data));
        setItems((prev) => {
          const temps = prev.filter((p) => String(p.id).startsWith("temp-"));
          const serverById = new Map(serverList.map((a) => [String(a.id), a]));
          const keptTemps = temps.filter((t) => !serverById.has(String(t.id)));
          const merged = [...keptTemps, ...serverList];
          recalcHasMore(merged, pageRef.current);
          return merged;
        });

        const stillPending = serverList.some((a) => a.url === "/spinner.gif");
        if (!stillPending || attempts >= 40) {
          stopPolling();
          void fetchQuota();
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          setError("Session expired. Please sign in again.");
          stopPolling();
          return;
        }
        if (attempts >= 40) {
          stopPolling();
          void fetchQuota();
        }
      }
    };

    void tick();
    pollTimerRef.current = setInterval(tick, 5000);
  }, [
    projectId,
    stopPolling,
    getToken,
    mapAssets,
    pickFeatureAssets,
    fetchQuota,
    recalcHasMore,
  ]);

  // Clear poll on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  // Initial + whenever projectId changes: hard refresh
  useEffect(() => {
    stopPolling();
    setItems([]);
    setError(null);
    setPage(1);
    pageRef.current = 1;
    setHasMore(true);
    void fetchProject();
    void fetchQuota();
  }, [projectId, fetchProject, fetchQuota, stopPolling]);

  // ✅ Only sync URL from dropdown when URL has no projectId (prevents notification bounce)
  useEffect(() => {
    if (!paramProjectId && selectedProduct?.id) {
      navigate(`/generate/color-restoration/${selectedProduct.id}`);
    }
  }, [selectedProduct, paramProjectId, navigate]);

  // ✅ Auto-start polling if pending items exist
  useEffect(() => {
    if (anyPendingLocal && projectId) beginPolling();
  }, [anyPendingLocal, beginPolling, projectId]);

  /* ---------------------------- Download ---------------------------- */
  const toCloudinaryDownloadUrl = useCallback((url) => {
    if (!url) return url;
    try {
      const u = new URL(url);
      if (u.hostname.includes("cloudinary") && u.pathname.includes("/upload/")) {
        u.pathname = u.pathname.replace("/upload/", "/upload/fl_attachment/");
        return u.toString();
      }
    } catch {
      // ignore
    }
    return url;
  }, []);

  const handleDownload = useCallback(
    (url) => {
      const dl = toCloudinaryDownloadUrl(url);
      const a = document.createElement("a");
      a.href = dl;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    [toCloudinaryDownloadUrl]
  );

  /* ---------------------------- Delete ---------------------------- */
  const handleDelete = useCallback(
    async (id) => {
      if (String(id).startsWith("temp-")) {
        setItems((prev) => {
          const next = prev.filter((x) => x.id !== id);
          recalcHasMore(next, pageRef.current);
          return next;
        });
        return;
      }

      if (!window.confirm("Delete this image?")) return;

      try {
        const token = await getToken();

        // ✅ IMPORTANT: Use feature soft-delete endpoint so the assets row stays in DB (quota does NOT decrease)
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/api/color-restoration/delete`,
          {
            headers: { Authorization: `Bearer ${token}` },
            data: { projectId, id },
          }
        );

        setItems((prev) => {
          const next = prev.filter((x) => String(x.id) !== String(id));
          recalcHasMore(next, pageRef.current);
          return next;
        });
        if (preview?.id === id) setPreview(null);

        // usage does not decrease; refresh for UI consistency
        void fetchQuota();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Delete failed:", e?.response?.data || e?.message);
        // eslint-disable-next-line no-alert
        alert("Delete failed");
      }
    },
    [getToken, preview, fetchQuota, projectId, recalcHasMore]
  );

  /* ---------------------------- Generate ---------------------------- */
  const handleGenerate = useCallback(
    async (imageUrl, quality) => {
      setError(null);

      // ✅ No analyzed project yet → send user to Analyze (still shows CTA on the page)
      if (!projectId) {
        setError(
          "Please analyze a product first so we can save your generated assets."
        );
        navigate("/analyze");
        return;
      }

      // Payment/block gate (server enforces too)
      if (quota?.is_blocked) {
        setError("Your account is blocked. Please contact support.");
        return;
      }
      if (!quota.unlimited && (quota?.blocked || quota?.payment_blocked)) {
        navigate("/pricing");
        return;
      }

      const canGenerateNow = quota.unlimited || (quota.remaining || 0) > 0;
      if (!canGenerateNow) {
        navigate("/pricing");
        return;
      }

      const tempId = `temp-${Date.now()}`;
      setItems((prev) => {
        const next = [{ id: tempId, url: "/spinner.gif", status: "pending" }, ...prev];
        recalcHasMore(next, pageRef.current);
        return next;
      });

      // optimistic quota decrement (UI only)
      setQuota((q) => {
        if (q.loading || q.unlimited) return q;
        const nextRemaining = Math.max(0, Number(q.remaining || 0) - 1);
        const nextUsed = Number(q.used || 0) + 1;
        return { ...q, remaining: nextRemaining, used: nextUsed };
      });

      // start polling immediately
      beginPolling();

      try {
        const token = await getToken();
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/color-restoration/restore`,
          { imageUrl, projectId, quality },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const placeholder = res?.data?.asset;
        if (placeholder?.id) {
          setItems((prev) => {
            const next = prev.map((it) =>
              it.id === tempId
                ? {
                    ...it,
                    id: placeholder.id,
                    url: "/spinner.gif",
                    status: "queued",
                  }
                : it
            );
            recalcHasMore(next, pageRef.current);
            return next;
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(
          "Color restoration queue failed:",
          e?.response?.data || e.message
        );

        const code = e?.response?.data?.code;
        if (code === "payment_required" || code === "account_blocked") {
          navigate("/pricing");
          return;
        }
        if (code === "images_quota_exceeded") {
          navigate("/pricing");
          return;
        }

        setError(e?.response?.data?.error || "Failed to restore colors.");

        // remove only this temp spinner
        setItems((prev) => {
          const next = prev.filter((x) => x.id !== tempId);
          recalcHasMore(next, pageRef.current);
          return next;
        });

        // resync quota
        void fetchQuota();
      }
    },
    [quota, navigate, projectId, beginPolling, getToken, fetchQuota, recalcHasMore]
  );

  /* --------------------------- Preview close --------------------------- */
  useEffect(() => {
    if (!preview) return undefined;

    const onKey = (e) => e.key === "Escape" && setPreview(null);
    const onClick = (e) => {
      if (previewBackdrop.current && e.target === previewBackdrop.current) {
        setPreview(null);
      }
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [preview]);

  /* --------------------------- Infinite Scroll --------------------------- */
  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPage((p) => p + 1);
    setLoadingMore(false);
  }, [loadingMore]);

  useEffect(() => {
    if (!hasMore || projectLoading || quota.loading || loadingMore) return;
    const el = loaderRef.current;
    if (!el) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "100px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, projectLoading, quota.loading, loadingMore, loadMore]);

  /* ------------------------------- quota UI ------------------------------ */
  const canGenerate =
    !quota.loading &&
    !quota.blocked &&
    !quota.payment_blocked &&
    !quota.is_blocked &&
    (quota.unlimited || (quota.remaining || 0) > 0);

  const totalImages = quota.unlimited
    ? Infinity
    : (quota.baseCap || 0) + (quota.imagesExtra || 0);
  const remaining = quota.unlimited ? Infinity : quota.remaining || 0;
  const used = quota.unlimited ? 0 : quota.used || 0;

  const showBar =
    !quota.loading &&
    !quota.unlimited &&
    Number.isFinite(totalImages) &&
    totalImages > 0;

  const pct = showBar
    ? Math.min(100, Math.max(0, Math.round((used / totalImages) * 100)))
    : 0;

  const isInitialLoading =
    (projectLoading || quota.loading) && !error && items.length === 0;

  /* ---------------------------------- UI ---------------------------------- */
  return (
    <div className="w-full px-2 sm:px-4 md:px-8 py-5 sm:py-8">
      <div className="max-w-[1600px] mx-auto bg-[#15152B] border border-[#2A2A40] rounded-3xl px-2 sm:px-4 md:px-8 py-6 sm:py-10 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Color Restoration
            </h1>
            <p className="text-white/70">
              Restore colors in old or faded images using Phot.AI.
            </p>
          </div>

          {!quota.loading && (
            <div
              className={`flex items-center gap-3 rounded-2xl px-4 py-2 border ${
                quota.unlimited
                  ? "border-pink-600/60 bg-pink-600/10"
                  : quota.blocked || quota.payment_blocked
                  ? "border-red-500/40 bg-red-500/10"
                  : "border-[#2A2A40] bg-[#1C1C34]"
              }`}
              title={
                quota.unlimited
                  ? "Unlimited images this cycle"
                  : quota.blocked || quota.payment_blocked
                  ? "Subscription inactive"
                  : "Images usage this cycle"
              }
            >
              {quota.unlimited ? (
                <span className="text-pink-400 font-semibold text-sm sm:text-base">
                  Unlimited images
                </span>
              ) : quota.blocked || quota.payment_blocked ? (
                <span className="text-red-300 font-semibold text-sm sm:text-base">
                  Subscription inactive
                </span>
              ) : (
                <span className="text-white/80 text-sm sm:text-base">
                  <span className="font-semibold text-white">{remaining}</span>{" "}
                  left of{" "}
                  <span className="font-semibold text-white">
                    {totalImages === Infinity ? "∞" : totalImages}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>

        {showBar && (
          <div className="mb-4 mt-2">
            <div className="h-2 w-full bg-[#21213a] rounded-full overflow-hidden border border-[#2A2A40]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background:
                    "linear-gradient(90deg, rgba(252,39,131,0.9) 0%, rgba(252,39,131,0.7) 100%)",
                }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-white/60">
              <span>Used: {used}</span>
              <span>Total: {totalImages === Infinity ? "∞" : totalImages}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 sm:mb-6 flex items-center gap-2 text-red-400">
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        )}

        {isInitialLoading ? (
          <div className="flex items-center justify-center py-10 sm:py-16">
            <Loader />
          </div>
        ) : (
          <div
            className="
              grid gap-4 sm:gap-6
              grid-cols-1
              xs:grid-cols-2
              md:grid-cols-3
              lg:grid-cols-4
              [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]
            "
          >
            {/* ✅ Create Card: ALWAYS shown.
                - No analyzed product → go to Analyze
                - No quota/subscription → go to Pricing
                - Otherwise open modal
            */}
            <div
              onClick={() => {
                if (!projectId) {
                  navigate("/analyze");
                  return;
                }
                if (!canGenerate) {
                  navigate("/pricing");
                  return;
                }
                setModalOpen(true);
              }}
              className={`flex flex-col items-center justify-center border-2 border-dashed border-[#3A3A50] text-white rounded-lg min-h-[160px] h-[210px] sm:h-[300px] ${
                !projectId || !canGenerate
                  ? "opacity-60 cursor-not-allowed"
                  : "cursor-pointer hover:border-pink-500 hover:text-pink-500"
              } transition text-center p-4 sm:p-6`}
              title={
                !projectId
                  ? "Analyze a product first to start generating"
                  : !canGenerate
                  ? quota.blocked || quota.payment_blocked
                    ? "Subscription inactive. Click to manage plan."
                    : "Out of images. Click to manage plan."
                  : "Pick an image and a quality preset"
              }
            >
              <Plus className="w-7 h-7 sm:w-8 sm:h-8 mb-2" />
              <p className="font-semibold text-base">Restore Colors</p>
              <p className="text-xs sm:text-sm text-gray-400 mt-1">
                {!projectId
                  ? "Analyze your first product to get started"
                  : quota.unlimited
                  ? "Unlimited images"
                  : quota.blocked || quota.payment_blocked
                  ? "Payment required to continue"
                  : `You have ${remaining} left of ${
                      totalImages === Infinity ? "∞" : totalImages
                    } this cycle`}
              </p>
              {!!projectId && !canGenerate && (
                <p className="text-xs text-red-300 mt-2">
                  {quota.blocked || quota.payment_blocked
                    ? "Your subscription is inactive. Update payment to continue."
                    : "You’ve reached your image limit. Upgrade or add images."}
                </p>
              )}
            </div>

            {/* Thumbnails */}
            {visibleItems.map(({ id, url, status }) => {
              const PENDING = new Set(["pending", "queued", "processing"]);
              const isPending = PENDING.has(status) || !url || url === "/spinner.gif";
              const displayUrl = isPending ? "/spinner.gif" : url;

              return (
                <div
                  key={id}
                  className="relative group bg-gradient-to-br from-[#1C1C2E] to-[#252538] border border-[#2A2A40] text-white rounded-2xl p-2 sm:p-3 flex items-center justify-center transition transform hover:scale-[1.03] hover:shadow-xl hover:ring-1 hover:ring-pink-500/30 min-h-[160px] h-[210px] sm:h-[300px]"
                  style={{ overflow: "hidden" }}
                >
                  {isPending ? (
                    <>
                      <img
                        src="/spinner.gif"
                        alt="Generating..."
                        className="object-contain w-full mx-auto rounded-lg opacity-70 animate-pulse"
                        draggable={false}
                        style={{
                          background: "#111124",
                          height: "100%",
                          maxHeight: "calc(100% - 32px)",
                        }}
                      />
                      <div className="absolute top-2 right-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDelete(id);
                          }}
                          className="bg-black/80 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <img
                        src={displayUrl}
                        alt="Color Restored"
                        className="object-contain max-h-[88%] w-full mx-auto rounded-lg transition-opacity duration-300 group-hover:opacity-70 cursor-pointer"
                        onClick={() => setPreview({ url: displayUrl, id })}
                        onError={(e) => {
                          e.currentTarget.src = placeholderImage;
                        }}
                        draggable={false}
                        style={{
                          background: "#111124",
                          height: "100%",
                          maxHeight: "calc(100% - 32px)",
                        }}
                      />

                      <div
                        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none z-20"
                        style={{ background: "rgba(21, 21, 43, 0.15)" }}
                      >
                        <div className="flex gap-3 pointer-events-auto">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(displayUrl);
                            }}
                            className="bg-black/80 hover:bg-pink-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Download"
                          >
                            <Download size={20} />
                          </button>
                          <a
                            href={displayUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="bg-black/80 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Open in new tab"
                          >
                            <ExternalLink size={20} />
                          </a>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleDelete(id);
                            }}
                            className="bg-black/80 hover:bg-red-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Delete"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ✅ Infinite loader (same pattern as ObjectRemover) */}
        {hasMore && !projectLoading && !quota.loading && (
          <div
            ref={loaderRef}
            className="w-full flex justify-center py-8 pointer-events-none"
          >
            {loadingMore && (
              <img src="/spinner.gif" alt="Loading..." className="w-7 h-7 mx-auto" />
            )}
          </div>
        )}

        {modalOpen && (
          <GenerateColorRestorationModal
            productImages={productImages}
            onClose={() => setModalOpen(false)}
            onGenerate={handleGenerate}
          />
        )}

        {/* Preview Modal */}
        {preview && (
          <div
            ref={previewBackdrop}
            className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center px-2 select-none"
          >
            <div className="relative bg-[#22223a] rounded-2xl max-w-[98vw] shadow-2xl flex flex-col">
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-pink-600 text-black hover:text-white rounded-full p-2 shadow transition"
                title="Close"
              >
                <X size={21} />
              </button>

              <div className="p-6 flex flex-col items-center justify-center">
                <img
                  src={preview.url}
                  alt="Preview"
                  className="max-w-[95vw] max-h-[78vh] object-contain rounded-xl bg-black"
                  style={{ boxShadow: "0 4px 32px 0 rgba(30,25,60,0.5)" }}
                  draggable={false}
                />
              </div>

              <div className="flex justify-center gap-4 pb-5">
                <button
                  type="button"
                  onClick={() => handleDownload(preview.url)}
                  className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Download
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete(preview.id)}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Delete
                </button>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Open in new tab
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
